// The Annex bays (P-18, docs/09 §7): two wings at the front corners of the Ministry model where
// the Annex rooms stand once a Resolution opens them. A closed bay is an empty lot — a foundation,
// survey strings and a signpost. Opening a room is a small construction: scaffolding drops, the
// floor and walls go up, the furniture arrives, the scaffolding lifts, the sign swings in and the
// lights come on. A room replaced by a newer one has its sign carried off and is taken down first.
//
// Pure presentation. BoardScene owns which room stands where (from the view) and registers each
// room's floor for picking and highlights.
import * as THREE from 'three';
import { gsap } from 'gsap';
import type { AnnexId, LocationId } from '@bcm/rules';
import { ANNEX_BAYS, annexLayout, annexRoomRect, type RoomLayout } from './layout';
import { ROOM_FLOOR_Y, TABLE_Y } from './ministry';
import { toonFrom } from './materials';
import { ashlar, glowPool, grass, herringbone, planks, tiles } from './textures';
import { plaqueTexture, roomBannerTexture } from './labels';
import type { PropLibrary, PropName, Tint } from './assets';
import { dust, floorRing, glint, motes } from './fx3d';
import { LOCATION_ICON } from '../game/icons';
import { C } from '../game/content';

// ─── Styles ──────────────────────────────────────────────────────────────────

interface AnnexStyle {
  tint: string;
  accent: string;
  floor: () => THREE.Texture;
  /** Metres per floor texture repeat. */
  tile: number;
}

/** Each room keeps one accent (its sign, panelling, light) distinct from the six Departments. */
export const ANNEX_STYLE: Record<AnnexId, AnnexStyle> = {
  'exchange-bureau': { tint: '#35584f', accent: '#48c2a6', floor: () => tiles('#2f5c54', '#35655c', 6, 'annex-exchange', [1, 1], true), tile: 5.5 },
  'lost-property': { tint: '#4d3f63', accent: '#b08ae0', floor: () => herringbone('#5a4668'), tile: 6 },
  'shredding-room': { tint: '#4a5a40', accent: '#9fc27a', floor: () => tiles('#5b6650', '#525d47', 6, 'annex-shred'), tile: 5 },
  'stamp-pound': { tint: '#6a3030', accent: '#e0503f', floor: () => tiles('#743430', '#6a2f2b', 6, 'annex-pound'), tile: 5.5 },
  'complaints-desk': { tint: '#6a4a30', accent: '#f09a4a', floor: () => planks('#6a4028', 'annex-complaints', [1, 1], 6, 0.1), tile: 6 },
  mailroom: { tint: '#34466a', accent: '#6aa0f0', floor: () => tiles('#2c3e60', '#34486c', 6, 'annex-mail'), tile: 5 },
};

export const annexStyle = (id: LocationId): AnnexStyle => ANNEX_STYLE[id as AnnexId];

const WALL_H = 1.1;
const WALL_T = 0.34;
const DOOR_W = 2.4;

const mat = {
  plinth: () => toonFrom({ color: '#2a1a12', roughness: 0.45 }),
  gold: () => toonFrom({ color: '#c9a14e', metalness: 1 }),
  wood: () => toonFrom({ color: '#7a5634' }),
  darkWood: () => toonFrom({ color: '#4a3020' }),
  iron: () => toonFrom({ color: '#2e2a28', metalness: 0.8 }),
  brass: () => toonFrom({ color: '#c9a14e', metalness: 0.9 }),
};

/** A horizontal plane whose UVs are world metres / `tile` (seamless like the Ministry's floors). */
function worldPlane(x0: number, x1: number, z0: number, z1: number, y: number, tile: number, m: THREE.Material): THREE.Mesh {
  const g = new THREE.PlaneGeometry(x1 - x0, z1 - z0);
  g.rotateX(-Math.PI / 2);
  const pos = g.attributes.position!;
  const uv = g.attributes.uv!;
  const cx = (x0 + x1) / 2;
  const cz = (z0 + z1) / 2;
  for (let i = 0; i < pos.count; i++) uv.setXY(i, (pos.getX(i) + cx) / tile, -(pos.getZ(i) + cz) / tile);
  const mesh = new THREE.Mesh(g, m);
  mesh.position.set(cx, y, cz);
  mesh.receiveShadow = true;
  return mesh;
}

function box(w: number, h: number, d: number, m: THREE.Material, x = 0, y = 0, z = 0, shadow = true): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
  mesh.position.set(x, y, z);
  mesh.castShadow = shadow;
  mesh.receiveShadow = true;
  return mesh;
}

/** A box whose UVs are world metres / `tile` on every face (masonry never stretches). */
function stoneBox(w: number, h: number, d: number, m: THREE.Material, tile = 2.4): THREE.Mesh {
  const geo = new THREE.BoxGeometry(w, h, d);
  const pos = geo.attributes.position!;
  const nor = geo.attributes.normal!;
  const uv = geo.attributes.uv!;
  for (let i = 0; i < pos.count; i++) {
    const [px, py, pz] = [pos.getX(i), pos.getY(i), pos.getZ(i)];
    if (Math.abs(nor.getY(i)) > 0.5) uv.setXY(i, px / tile, pz / tile);
    else if (Math.abs(nor.getX(i)) > 0.5) uv.setXY(i, pz / tile, py / tile);
    else uv.setXY(i, px / tile, py / tile);
  }
  const mesh = new THREE.Mesh(geo, m);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

// ─── The bay (plinth and empty lot) ──────────────────────────────────────────

export class AnnexBay {
  readonly group = new THREE.Group();
  /** The empty lot: shown while no room stands here. */
  readonly lot = new THREE.Group();
  room: AnnexRoom | null = null;
  /** A construction or demolition is playing: BoardScene's sync leaves the bay alone. */
  busy = false;

  constructor(
    readonly index: number,
    parent: THREE.Object3D,
  ) {
    const b = ANNEX_BAYS[index]!;
    this.group.name = `annex-bay-${index}`;
    const [x0, x1] = b.x;
    const [z0, z1] = b.z;
    const w = x1 - x0;
    const d = z1 - z0;
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    // The wing's plinth, matching the Ministry's: walnut body, gilt trims, a lawn on top.
    const plinth = mat.plinth();
    const gold = mat.gold();
    this.group.add(box(w + 0.8, 0.16, d + 0.8, plinth, cx, TABLE_Y + 0.08, cz));
    this.group.add(box(w, -TABLE_Y - 0.11, d, plinth, cx, TABLE_Y / 2 - 0.055 + 0.1, cz));
    for (const [tw, td, tx, tz] of [
      [w + 0.12, 0.1, cx, z0],
      [w + 0.12, 0.1, cx, z1],
      [0.1, d + 0.12, x0, cz],
      [0.1, d + 0.12, x1, cz],
    ] as const) {
      this.group.add(box(tw, 0.08, td, gold, tx, -0.02, tz, false));
      this.group.add(box(tw + 0.8 * Math.sign(tw - 1), 0.05, td + 0.8 * Math.sign(td - 1), gold, tx, TABLE_Y + 0.17, tz, false));
    }
    this.group.add(worldPlane(x0, x1, z0, z1, 0, 6, toonFrom({ map: grass(), roughness: 0.95 })));
    // A gravel path from the room's door to the wing's inner edge (toward the Ministry).
    const r = annexRoomRect(index);
    const doorZ = (r.z[0] + r.z[1]) / 2;
    const [px0, px1] = b.toward < 0 ? [x0, r.x[0]] : [r.x[1], x1];
    this.group.add(worldPlane(px0, px1, doorZ - 1.1, doorZ + 1.1, 0.012, 3, toonFrom({ color: '#a89a80' })));
    this.buildLot();
    this.group.add(this.lot);
    this.group.visible = false;
    parent.add(this.group);
  }

  /** The empty lot: a foundation kerb, chalk lines, survey stakes and strings, a signpost. */
  private buildLot(): void {
    const r = annexRoomRect(this.index);
    const b = ANNEX_BAYS[this.index]!;
    const kerb = toonFrom({ map: ashlar('#8d8479') });
    const [x0, x1] = r.x;
    const [z0, z1] = r.z;
    const w = x1 - x0;
    const d = z1 - z0;
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    // Kerb along the foundation, a gap where the door will be.
    const doorX = b.toward < 0 ? x0 : x1;
    const runs: [number, number, number, number][] = [
      [cx, z0, w, 0.3],
      [cx, z1, w, 0.3],
      [x0 + x1 - doorX, cz, 0.3, d],
      [doorX, (z0 + cz - DOOR_W / 2) / 2, 0.3, cz - DOOR_W / 2 - z0],
      [doorX, (cz + DOOR_W / 2 + z1) / 2, 0.3, z1 - cz - DOOR_W / 2],
    ];
    for (const [x, z, bw, bd] of runs) {
      const k = stoneBox(bw, 0.14, bd, kerb);
      k.position.set(x, 0.07, z);
      this.lot.add(k);
    }
    // A pale foundation slab inside, with chalked guide lines.
    const slab = worldPlane(x0 + 0.2, x1 - 0.2, z0 + 0.2, z1 - 0.2, 0.02, 4, toonFrom({ color: '#b7ad98' }));
    this.lot.add(slab);
    const chalk = new THREE.MeshBasicMaterial({ color: '#f4efe2', transparent: true, opacity: 0.28, depthWrite: false });
    for (const t of [0.33, 0.66]) {
      const l1 = new THREE.Mesh(new THREE.PlaneGeometry(w - 0.8, 0.04), chalk);
      l1.rotation.x = -Math.PI / 2;
      l1.position.set(cx, 0.03, z0 + d * t);
      const l2 = new THREE.Mesh(new THREE.PlaneGeometry(0.04, d - 0.8), chalk);
      l2.rotation.x = -Math.PI / 2;
      l2.position.set(x0 + w * t, 0.03, cz);
      this.lot.add(l1, l2);
    }
    // Survey stakes at the corners with a string between them.
    const wood = mat.wood();
    const string = toonFrom({ color: '#f0e6cc' });
    const corners: [number, number][] = [
      [x0 - 0.25, z0 - 0.25],
      [x1 + 0.25, z0 - 0.25],
      [x1 + 0.25, z1 + 0.25],
      [x0 - 0.25, z1 + 0.25],
    ];
    corners.forEach(([sx, sz], i) => {
      const stake = box(0.09, 0.7, 0.09, wood, sx, 0.35, sz);
      const flag = box(0.02, 0.12, 0.2, toonFrom({ color: '#e0503f' }), sx, 0.62, sz + 0.1, false);
      this.lot.add(stake, flag);
      const [nx, nz] = corners[(i + 1) % 4]!;
      const len = Math.hypot(nx - sx, nz - sz);
      const s = box(len, 0.018, 0.018, string, (sx + nx) / 2, 0.5, (sz + nz) / 2, false);
      s.rotation.y = -Math.atan2(nz - sz, nx - sx);
      this.lot.add(s);
    });
    // A little stack of bricks and planks waiting by the path.
    const brick = toonFrom({ color: '#9a4a36' });
    const bx = b.toward < 0 ? x0 + 1.2 : x1 - 1.2;
    for (let i = 0; i < 6; i++) this.lot.add(box(0.46, 0.16, 0.22, brick, bx + (i % 3) * 0.48 - 0.48, 0.1 + Math.floor(i / 3) * 0.17, z1 - 1.2));
    for (let i = 0; i < 3; i++) this.lot.add(box(2.2, 0.06, 0.24, wood, bx, 0.04 + i * 0.07, z0 + 1.0 + i * 0.03));
    // The signpost: which bay this is, and that it waits for a Resolution.
    const post = box(0.1, 1.6, 0.1, mat.darkWood(), b.toward < 0 ? x0 + 0.1 : x1 - 0.1, 0.8, z1 + 0.45);
    const tex = plaqueTexture({ title: `Annex · Bay ${this.index === 0 ? 'I' : 'II'}`, subtitle: 'Awaiting a Resolution', width: 640, accent: '#9b7a45' });
    const board = new THREE.Mesh(new THREE.PlaneGeometry(2.1, (2.1 * tex.image.height) / tex.image.width), new THREE.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false }));
    board.position.set(post.position.x + (b.toward < 0 ? 1.0 : -1.0), 1.45, z1 + 0.5);
    board.rotation.x = -0.75; // leaned back toward the camera
    this.lot.add(post, board);
  }

  /** Fade the lot in or out (it steps aside while a room is built on it). */
  showLot(on: boolean, animate: boolean, speed = 1): void {
    gsap.killTweensOf(this.lot.scale);
    if (!animate) {
      this.lot.visible = on;
      this.lot.scale.set(1, 1, 1);
      return;
    }
    this.lot.visible = true;
    gsap.fromTo(this.lot.scale, { y: on ? 0.001 : 1 }, { y: on ? 1 : 0.001, duration: 0.35 / speed, ease: on ? 'back.out(2)' : 'power2.in', onComplete: () => void (this.lot.visible = on) });
  }
}

// ─── Rooms ───────────────────────────────────────────────────────────────────

interface Piece {
  /** A prop from the library, or a procedural piece. */
  p: PropName | Custom;
  /** Across the room from the door wall (0) to the far wall (1), and from the back (0) to the front (1). */
  u: number;
  v: number;
  /** Degrees; mirrored with the room (a door on the west or on the east). */
  rot?: number;
  s?: number;
  tint?: Tint;
  y?: number;
}

type Custom = 'exchangeBoard' | 'umbrellaStand' | 'shredder' | 'paperStack' | 'stampPen' | 'queuePosts' | 'ticketPost' | 'mailSacks' | 'bin' | 'scales';

/** Furniture per room. The door side and the front third (the stations) stay clear. */
const FURNISH: Record<AnnexId, Piece[]> = {
  'exchange-bureau': [
    { p: 'stamp_counter', u: 0.55, v: 0.12 },
    { p: 'exchangeBoard', u: 0.55, v: 0.02 },
    { p: 'display_case', u: 0.93, v: 0.45, rot: -90 },
    { p: 'scales', u: 0.8, v: 0.5 },
    { p: 'crates', u: 0.9, v: 0.12 },
    { p: 'plant', u: 0.07, v: 0.08 },
    { p: 'candelabra', u: 0.93, v: 0.9 },
  ],
  'lost-property': [
    { p: 'pigeonholes', u: 0.38, v: 0.07 },
    { p: 'bookshelf_low', u: 0.78, v: 0.06 },
    { p: 'umbrellaStand', u: 0.14, v: 0.28 },
    { p: 'crates', u: 0.9, v: 0.34 },
    { p: 'barrels', u: 0.92, v: 0.62 },
    { p: 'book_pile', u: 0.62, v: 0.38, rot: 25 },
    { p: 'globe', u: 0.08, v: 0.08 },
  ],
  'shredding-room': [
    { p: 'shredder', u: 0.55, v: 0.22 },
    { p: 'bin', u: 0.74, v: 0.3 },
    { p: 'filing_cabinet_tall', u: 0.86, v: 0.07 },
    { p: 'filing_cabinet_tall', u: 0.96, v: 0.2, rot: -90 },
    { p: 'paperStack', u: 0.28, v: 0.12 },
    { p: 'paperStack', u: 0.88, v: 0.5 },
    { p: 'plant', u: 0.07, v: 0.08 },
  ],
  'stamp-pound': [
    { p: 'stampPen', u: 0.58, v: 0.3 },
    { p: 'desk', u: 0.86, v: 0.1 },
    { p: 'chair', u: 0.86, v: 0.02 },
    { p: 'banner', u: 0.3, v: 0.005, y: 0.75, tint: { accent: '#9a2f35' } },
    { p: 'filing_cabinet', u: 0.1, v: 0.1 },
    { p: 'candles', u: 0.94, v: 0.55 },
  ],
  'complaints-desk': [
    { p: 'desk', u: 0.58, v: 0.18 },
    { p: 'chair', u: 0.58, v: 0.08 },
    { p: 'queuePosts', u: 0.34, v: 0.44 },
    { p: 'ticketPost', u: 0.2, v: 0.3 },
    { p: 'book_pile', u: 0.84, v: 0.26, rot: -15 },
    { p: 'book_pile', u: 0.9, v: 0.5, rot: 40 },
    { p: 'filing_cabinet', u: 0.93, v: 0.1 },
    { p: 'banner', u: 0.3, v: 0.005, y: 0.75, tint: { accent: '#b8692a' } },
  ],
  mailroom: [
    { p: 'pigeonholes', u: 0.5, v: 0.07 },
    { p: 'pipes', u: 0.88, v: 0.03, tint: { glow: '#6aa0f0', accent: '#3a5a8a' } },
    { p: 'meeting_table', u: 0.52, v: 0.38, s: 0.8 },
    { p: 'mailSacks', u: 0.88, v: 0.52 },
    { p: 'crates', u: 0.14, v: 0.12 },
    { p: 'plant', u: 0.07, v: 0.9 },
  ],
};

export class AnnexRoom {
  readonly group = new THREE.Group();
  /** Scales with the construction: floor, border, stations. */
  readonly floorGroup = new THREE.Group();
  readonly floor: THREE.Mesh;
  readonly layout: RoomLayout;
  private walls: THREE.Group[] = [];
  private pieces: THREE.Object3D[] = [];
  private furnished = false;
  /** The sign hangs from this pivot (its top edge), so it can swing. */
  private signPivot = new THREE.Group();
  private signArm = new THREE.Group();
  private pool: THREE.Mesh;
  private poolMat: THREE.MeshBasicMaterial;
  /**
   * The room's lights: wall sconces that come on with the light pool. No point lights — a new one
   * would recompile every lit material (a hitch) and cost every pixel on phones.
   */
  private lamps: THREE.Object3D[] = [];
  private scaffold: THREE.Group | null = null;
  readonly accent: string;
  /** The finished room is up (the label and highlights may show). */
  ready = false;

  constructor(
    readonly location: LocationId,
    readonly bay: AnnexBay,
    lib: PropLibrary | null,
  ) {
    const style = annexStyle(location);
    this.accent = style.accent;
    this.layout = annexLayout(location, bay.index, style.tint, style.accent);
    const r = annexRoomRect(bay.index);
    const [x0, x1] = r.x;
    const [z0, z1] = r.z;
    const cx = (x0 + x1) / 2;
    const cz = (z0 + z1) / 2;
    this.group.name = `annex-room-${location}`;

    // Floor, and a darker border inlay just inside the walls (as every room has).
    this.floor = worldPlane(x0 + WALL_T / 2, x1 - WALL_T / 2, z0 + WALL_T / 2, z1 - WALL_T / 2, ROOM_FLOOR_Y, style.tile, toonFrom({ map: style.floor(), roughness: 0.5 }));
    this.floor.userData.location = location;
    // The floor group scales from the room's centre.
    this.floorGroup.position.set(cx, 0, cz);
    this.floor.position.x -= cx;
    this.floor.position.z -= cz;
    this.floorGroup.add(this.floor);
    const border = new THREE.MeshBasicMaterial({ color: new THREE.Color(style.tint).multiplyScalar(0.55), transparent: false });
    const bw = 0.3;
    for (const [w, d, x, z] of [
      [x1 - x0 - 0.8, bw, cx, z0 + 0.4 + bw / 2],
      [x1 - x0 - 0.8, bw, cx, z1 - 0.4 - bw / 2],
      [bw, z1 - z0 - 0.8, x0 + 0.4 + bw / 2, cz],
      [bw, z1 - z0 - 0.8, x1 - 0.4 - bw / 2, cz],
    ] as const) {
      const band = new THREE.Mesh(new THREE.PlaneGeometry(w, d), border);
      band.rotation.x = -Math.PI / 2;
      band.position.set(x - cx, ROOM_FLOOR_Y + 0.004, z - cz);
      this.floorGroup.add(band);
    }
    this.group.add(this.floorGroup);

    this.buildWalls(style);
    this.buildSign(style);
    // A soft pool of the room's light on its floor (lit with the lights).
    this.poolMat = new THREE.MeshBasicMaterial({ map: glowPool(), color: style.accent, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
    this.pool = new THREE.Mesh(new THREE.PlaneGeometry(7, 7), this.poolMat);
    this.pool.rotation.x = -Math.PI / 2;
    this.pool.position.set(cx, ROOM_FLOOR_Y + 0.03, cz - 0.6);
    this.pool.renderOrder = 2;
    this.group.add(this.pool);
    if (lib) this.furnish(lib);
    bay.group.add(this.group);
  }

  /** Room-space → world: `u` from the door wall to the far wall, `v` from the back to the front. */
  private at(u: number, v: number): [number, number] {
    const r = annexRoomRect(this.bay.index);
    const inset = WALL_T / 2 + 0.3;
    const toward = ANNEX_BAYS[this.bay.index]!.toward;
    const [a, b] = toward < 0 ? [r.x[0] + inset, r.x[1] - inset] : [r.x[1] - inset, r.x[0] + inset];
    return [a + (b - a) * u, r.z[0] + inset + (r.z[1] - r.z[0] - 2 * inset) * v];
  }

  private buildWalls(style: AnnexStyle): void {
    const r = annexRoomRect(this.bay.index);
    const b = ANNEX_BAYS[this.bay.index]!;
    const [x0, x1] = r.x;
    const [z0, z1] = r.z;
    const cz = (z0 + z1) / 2;
    const stone = toonFrom({ map: ashlar('#a39a8d') });
    const base = toonFrom({ map: ashlar('#6f675e') });
    const coping = toonFrom({ map: ashlar('#968a78') });
    const panel = toonFrom({ color: new THREE.Color(style.accent).lerp(new THREE.Color('#3a2616'), 0.62) });
    const rail = toonFrom({ color: '#8a6d3c' });
    const doorX = b.toward < 0 ? x0 : x1;
    // Each run is its own piece so the walls can rise one after another.
    type Run = { axis: 'x' | 'z'; at: number; a: number; b: number; into: number };
    const runs: Run[] = [
      { axis: 'z', at: z0, a: x0, b: x1, into: 1 },
      { axis: 'x', at: x0 + x1 - doorX, a: z0, b: z1, into: doorX === x0 ? -1 : 1 },
      { axis: 'z', at: z1, a: x0, b: x1, into: -1 },
      { axis: 'x', at: doorX, a: z0, b: cz - DOOR_W / 2, into: doorX === x0 ? 1 : -1 },
      { axis: 'x', at: doorX, a: cz + DOOR_W / 2, b: z1, into: doorX === x0 ? 1 : -1 },
    ];
    for (const run of runs) {
      const g = new THREE.Group();
      const len = run.b - run.a + (run.axis === 'z' ? WALL_T : 0);
      const mid = (run.a + run.b) / 2;
      const [x, z] = run.axis === 'z' ? [mid, run.at] : [run.at, mid];
      const dim = (l: number, t: number): [number, number] => (run.axis === 'z' ? [l, t] : [t, l]);
      const [bw, bd] = dim(len + 0.08, WALL_T + 0.1);
      const baseMesh = stoneBox(bw, 0.24, bd, base);
      baseMesh.position.y = 0.12;
      const [ww, wd] = dim(len, WALL_T);
      const body = stoneBox(ww, WALL_H - 0.24, wd, stone);
      body.position.y = 0.24 + (WALL_H - 0.24) / 2;
      const [cw, cd] = dim(len + 0.12, WALL_T + 0.14);
      const cap = box(cw, 0.1, cd, coping);
      cap.position.y = WALL_H + 0.05;
      g.add(baseMesh, body, cap);
      // Panelling and a brass rail on the inner face.
      const inner = run.into * (WALL_T / 2 + 0.035);
      const pl = Math.max(0.1, run.b - run.a - 0.2);
      const [pw, pd] = dim(pl, 0.07);
      const pan = box(pw, 0.72, pd, panel, 0, 0.4, 0, false);
      const [rw, rd] = dim(pl, 0.08);
      const rl = box(rw, 0.06, rd, rail, 0, 0.8, 0, false);
      if (run.axis === 'z') {
        pan.position.z = inner;
        rl.position.z = inner * 1.1;
      } else {
        pan.position.x = inner;
        rl.position.x = inner * 1.1;
      }
      g.add(pan, rl);
      // Pilasters at both ends.
      for (const e of [-1, 1]) {
        const pil = stoneBox(0.46, WALL_H + 0.18, 0.46, base);
        const off = (e * (run.b - run.a)) / 2;
        pil.position.set(run.axis === 'z' ? off : 0, (WALL_H + 0.18) / 2, run.axis === 'z' ? 0 : off);
        g.add(pil);
      }
      g.position.set(x, 0, z);
      this.walls.push(g);
      this.group.add(g);
    }
    // The doorway: gateposts with a brass lintel.
    const gold = mat.gold();
    for (const e of [-1, 1]) {
      const post = stoneBox(0.5, WALL_H + 0.5, 0.5, base);
      post.position.set(doorX, (WALL_H + 0.5) / 2, cz + e * (DOOR_W / 2 + 0.05));
      const g = new THREE.Group();
      g.add(post);
      this.walls.push(g);
      this.group.add(g);
    }
    const lintel = new THREE.Group();
    lintel.add(box(0.36, 0.16, DOOR_W + 0.6, gold, doorX, WALL_H + 0.58, cz, false));
    this.walls.push(lintel);
    this.group.add(lintel);
  }

  /** The room's sign: a painted board on an iron bracket at the front corner by the door. */
  private buildSign(style: AnnexStyle): void {
    const r = annexRoomRect(this.bay.index);
    const b = ANNEX_BAYS[this.bay.index]!;
    const def = C.locations.get(this.location)!;
    const iron = mat.iron();
    const postX = b.toward < 0 ? r.x[0] - 0.35 : r.x[1] + 0.35;
    const z = r.z[1] + 0.35;
    const post = box(0.12, 3.25, 0.12, iron, 0, 1.62, 0);
    const cap = new THREE.Mesh(new THREE.OctahedronGeometry(0.12, 0), mat.gold());
    cap.position.y = 3.3;
    const armLen = 3.9;
    const dir = -b.toward; // the arm reaches in over the front wall
    const arm = box(armLen, 0.08, 0.08, iron, (dir * armLen) / 2, 3.0, 0);
    const brace = box(1.3, 0.06, 0.06, iron, dir * 0.45, 2.62, 0);
    brace.rotation.z = dir * 0.62;
    this.signArm.add(post, cap, arm, brace);
    this.signArm.position.set(postX, 0, z);
    // The board hangs from two chains; its pivot is the arm.
    const tex = roomBannerTexture({ title: def.name.replace(/^The /, ''), subtitle: def.text, accent: style.accent, icon: LOCATION_ICON[this.location] });
    const bw = 3.6;
    const bh = (bw * tex.image.height) / tex.image.width;
    const face = new THREE.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false });
    const board = new THREE.Mesh(new THREE.PlaneGeometry(bw, bh), face);
    board.position.set(0, -0.36 - bh / 2, 0.03);
    const back = box(bw * 0.93, bh * 0.72, 0.05, mat.darkWood(), 0, -0.36 - bh / 2, 0, false);
    for (const e of [-1, 1]) {
      const chain = box(0.03, 0.4, 0.03, iron, e * bw * 0.36, -0.2, 0, false);
      this.signPivot.add(chain);
    }
    this.signPivot.add(board, back);
    this.signPivot.position.set(dir * (armLen - 2.0), 2.98, 0);
    this.signPivot.rotation.x = SIGN_REST;
    this.signArm.add(this.signPivot);
    this.group.add(this.signArm);
  }

  /** Bring in the furniture (when the prop library arrives after the room was built). */
  furnish(lib: PropLibrary): void {
    if (this.furnished) return;
    this.furnished = true;
    const mirror = ANNEX_BAYS[this.bay.index]!.toward < 0 ? 1 : -1;
    // Two sconces on the back wall.
    const r = annexRoomRect(this.bay.index);
    for (const u of [0.22, 0.78]) {
      const [x] = this.at(u, 0);
      const lamp = lib.has('sconce') ? lib.make('sconce') : new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), toonFrom({ color: '#ffd9a0', emissive: '#ffb45a', emissiveIntensity: 1.2 }));
      lamp.position.set(x, lib.has('sconce') ? 0 : 1.4, r.z[0] + WALL_T / 2 + 0.02);
      lamp.userData.baseScale = 1;
      this.lamps.push(lamp);
      this.group.add(lamp);
    }
    for (const pc of FURNISH[this.location as AnnexId] ?? []) {
      const [x, z] = this.at(pc.u, pc.v);
      const o = isCustom(pc.p) ? custom(pc.p, this.accent) : lib.make(pc.p, pc.tint ?? { accent: this.accent, glow: this.accent });
      o.position.set(x, ROOM_FLOOR_Y + (pc.y ?? 0), z);
      o.rotation.y = THREE.MathUtils.degToRad((pc.rot ?? 0) * mirror);
      if (pc.s) o.scale.setScalar(pc.s);
      o.userData.baseScale = pc.s ?? 1;
      o.traverse((c) => {
        const m = c as THREE.Mesh;
        if (m.isMesh) {
          m.castShadow = true;
          m.receiveShadow = true;
        }
      });
      this.pieces.push(o);
      this.group.add(o);
    }
  }

  /** Everything in place at once (a snapshot or a reconnect: no construction to watch). */
  showInstant(): void {
    this.ready = true;
    this.floorGroup.scale.set(1, 1, 1);
    for (const w of this.walls) w.scale.set(1, 1, 1);
    for (const p of this.pieces) p.scale.setScalar(p.userData.baseScale ?? 1);
    this.signPivot.rotation.x = SIGN_REST;
    this.signArm.visible = true;
    this.signArm.scale.set(1, 1, 1);
    this.poolMat.opacity = POOL_OPACITY;
    for (const l of this.lamps) {
      l.visible = true;
      l.scale.setScalar(1);
    }
  }

  /** Where the camera, the label and effects anchor: the room's centre. */
  center(y = 0): THREE.Vector3 {
    const r = annexRoomRect(this.bay.index);
    return new THREE.Vector3((r.x[0] + r.x[1]) / 2, y, (r.z[0] + r.z[1]) / 2);
  }

  /**
   * The construction (docs/09 §7.1, ~2.6 s at speed 1): scaffolding drops onto the lot, the floor
   * spreads and the walls rise, the furniture arrives, the scaffolding lifts away, the sign swings
   * in and the lights come on. `cue` hears each moment for its sound.
   */
  build(fx: THREE.Object3D, speed: number, reduced: boolean, cue: (c: 'land' | 'wall' | 'prop' | 'sign' | 'lights') => void): Promise<void> {
    const r = annexRoomRect(this.bay.index);
    const [x0, x1] = r.x;
    const [z0, z1] = r.z;
    const center = this.center();
    // Start: nothing stands.
    this.floorGroup.scale.set(0.001, 1, 0.001);
    for (const w of this.walls) w.scale.set(1, 0.001, 1);
    for (const p of this.pieces) p.scale.setScalar(0.001);
    this.signArm.visible = false;
    this.poolMat.opacity = 0;
    for (const l of this.lamps) l.visible = false;
    const scaffold = makeScaffold(x0, x1, z0, z1);
    this.scaffold = scaffold;
    this.group.add(scaffold);

    const tl = gsap.timeline();
    tl.timeScale(reduced ? speed * 1.6 : speed);
    // 1 · Scaffolding drops, pole by pole, onto the lot; each landing kicks up dust.
    const poles = scaffold.children.filter((c) => c.userData.pole);
    poles.forEach((p, i) => {
      const y = p.position.y;
      p.position.y = y + 9;
      tl.to(p.position, { y, duration: 0.42, ease: 'bounce.out' }, i * 0.045);
      tl.call(() => {
        if (i % 2 === 0) cue('land');
        dust(fx, new THREE.Vector3(p.position.x, 0.05, p.position.z), { count: 3, size: 0.8, speed, reduced });
      }, [], i * 0.045 + 0.2);
    });
    const decks = scaffold.children.filter((c) => !c.userData.pole);
    decks.forEach((d, i) => {
      const y = d.position.y;
      d.position.y = y + 7;
      tl.to(d.position, { y, duration: 0.36, ease: 'back.out(1.4)' }, 0.25 + i * 0.06);
    });
    // 2 · The floor spreads out from the centre; the walls rise, one run after another.
    tl.to(this.floorGroup.scale, { x: 1, z: 1, duration: 0.5, ease: 'power3.out' }, 0.5);
    tl.call(() => void floorRing(fx, center.clone().setY(ROOM_FLOOR_Y + 0.05), { color: '#f4e2b0', radius: 4.6, speed: speed * 1.2, opacity: 0.35 }), [], 0.5);
    this.walls.forEach((w, i) => {
      const t = 0.62 + i * 0.07;
      tl.to(w.scale, { y: 1, duration: 0.36, ease: 'back.out(1.6)' }, t);
      if (i < 5) {
        tl.call(() => {
          cue('wall');
          const p = w.position.clone().setY(0.1);
          dust(fx, p, { count: 4, size: 1.1, speed, reduced });
        }, [], t);
      }
    });
    // 3 · The furniture arrives.
    this.pieces.forEach((p, i) => {
      const t = 1.08 + i * 0.07;
      const s = p.userData.baseScale ?? 1;
      tl.to(p.scale, { x: s, y: s, z: s, duration: 0.34, ease: 'back.out(2.2)' }, t);
      if (i % 2 === 0) tl.call(() => cue('prop'), [], t);
    });
    // 4 · The scaffolding lifts away.
    const lift = 1.55 + this.pieces.length * 0.035;
    tl.call(() => void ((scaffold.userData.mat as THREE.Material).depthWrite = false), [], lift);
    scaffold.children.forEach((c, i) => {
      tl.to(c.position, { y: c.position.y + 6, duration: 0.45, ease: 'power2.in' }, lift + (i % 6) * 0.025);
    });
    tl.to(scaffold.userData.mat as THREE.Material, { opacity: 0, duration: 0.35, ease: 'power1.in' }, lift + 0.12);
    tl.call(() => {
      this.group.remove(scaffold);
      disposeTree(scaffold);
      this.scaffold = null;
    }, [], lift + 0.5);
    // 5 · The sign swings in on its chains.
    const sign = lift + 0.18;
    tl.call(() => {
      this.signArm.visible = true;
      cue('sign');
    }, [], sign);
    tl.fromTo(this.signArm.scale, { y: 0.001 }, { y: 1, duration: 0.22, ease: 'power2.out' }, sign);
    tl.fromTo(this.signPivot.rotation, { x: SIGN_REST - 1.35 }, { x: SIGN_REST, duration: 1.1, ease: 'elastic.out(1, 0.32)' }, sign + 0.1);
    // 6 · Lights on: a flicker, then steady; a ring of light across the floor and a few motes.
    const lights = sign + 0.35;
    tl.call(() => cue('lights'), [], lights);
    // A flicker, then steady: the sconces blink on, the pool of light fills the floor.
    for (const [dt, on] of [
      [0, true],
      [0.09, false],
      [0.16, true],
    ] as const) {
      tl.call(() => void this.lamps.forEach((l) => (l.visible = on)), [], lights + dt);
    }
    tl.to(this.poolMat, { opacity: POOL_OPACITY * 1.6, duration: 0.08 }, lights)
      .to(this.poolMat, { opacity: POOL_OPACITY * 0.3, duration: 0.07 }, lights + 0.09)
      .to(this.poolMat, { opacity: POOL_OPACITY, duration: 0.45, ease: 'power2.out' }, lights + 0.16);
    tl.call(() => {
      void floorRing(fx, center.clone().setY(ROOM_FLOOR_Y + 0.06), { color: this.accent, radius: 5.2, speed, opacity: 0.55 });
      motes(fx, center.clone().setY(0.4), { count: 14, spread: 2.8, color: '#ffe6ad', rise: 2.4, speed, reduced });
      glint(fx, this.signPivot.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.4, 0.2)), { size: 2.2, speed });
    }, [], lights + 0.2);
    return new Promise((resolve) =>
      tl.eventCallback('onComplete', () => {
        this.showInstant();
        resolve();
      }),
    );
  }

  /** Taken down for a newer room (~0.9 s): the sign is carried off, then everything sinks away. */
  demolish(fx: THREE.Object3D, speed: number, reduced: boolean, cue: (c: 'sign' | 'wall') => void): Promise<void> {
    this.ready = false;
    const b = ANNEX_BAYS[this.bay.index]!;
    const tl = gsap.timeline();
    tl.timeScale(reduced ? speed * 1.6 : speed);
    // The sign unhooks and is carried off the edge of the table.
    tl.call(() => cue('sign'), [], 0);
    tl.to(this.signPivot.rotation, { x: SIGN_REST - 0.5, z: -b.toward * 0.4, duration: 0.2, ease: 'power2.out' }, 0);
    tl.to(this.signPivot.position, { y: this.signPivot.position.y + 4.5, x: this.signPivot.position.x - b.toward * 5, duration: 0.7, ease: 'power2.in' }, 0.12);
    tl.to(this.signPivot.scale, { x: 0.3, y: 0.3, z: 0.3, duration: 0.6, ease: 'power2.in' }, 0.2);
    tl.to(this.signArm.scale, { y: 0.001, duration: 0.25, ease: 'power2.in' }, 0.45);
    tl.to(this.poolMat, { opacity: 0, duration: 0.3 }, 0.2);
    tl.call(() => void this.lamps.forEach((l) => (l.visible = false)), [], 0.25);
    // Then the furniture, the walls and the floor.
    this.pieces.forEach((p, i) => tl.to(p.scale, { x: 0.001, y: 0.001, z: 0.001, duration: 0.22, ease: 'back.in(2)' }, 0.3 + i * 0.03));
    this.walls.forEach((w, i) => {
      const t = 0.45 + i * 0.04;
      tl.to(w.scale, { y: 0.001, duration: 0.26, ease: 'power2.in' }, t);
      if (i < 5 && i % 2 === 0) {
        tl.call(() => {
          cue('wall');
          dust(fx, w.position.clone().setY(0.1), { count: 4, size: 1.1, speed, reduced });
        }, [], t + 0.2);
      }
    });
    tl.to(this.floorGroup.scale, { x: 0.001, z: 0.001, duration: 0.3, ease: 'power2.in' }, 0.7);
    return new Promise((resolve) => tl.eventCallback('onComplete', () => resolve()));
  }

  dispose(): void {
    this.bay.group.remove(this.group);
    if (this.scaffold) disposeTree(this.scaffold);
    // Geometry only: materials from the prop library are shared.
    this.group.traverse((o) => (o as THREE.Mesh).geometry?.dispose?.());
    this.poolMat.dispose();
  }
}

const SIGN_REST = -0.62;
const POOL_OPACITY = 0.34;

function disposeTree(o: THREE.Object3D): void {
  o.traverse((c) => {
    const m = c as THREE.Mesh;
    m.geometry?.dispose?.();
  });
}

/** Timber scaffolding around the room: poles at the corners and mid-sides, two decks of planks. */
function makeScaffold(x0: number, x1: number, z0: number, z1: number): THREE.Group {
  const g = new THREE.Group();
  const m = toonFrom({ color: '#b08a58', transparent: true, opacity: 1 });
  g.userData.mat = m;
  const cx = (x0 + x1) / 2;
  const cz = (z0 + z1) / 2;
  const h = 3.4;
  const spots: [number, number][] = [
    [x0, z0],
    [cx, z0],
    [x1, z0],
    [x1, cz],
    [x1, z1],
    [cx, z1],
    [x0, z1],
    [x0, cz],
  ];
  for (const [x, z] of spots) {
    const pole = box(0.14, h, 0.14, m, x, h / 2, z);
    pole.userData.pole = true;
    g.add(pole);
  }
  for (const y of [1.3, 2.6]) {
    for (const [w, d, x, z] of [
      [x1 - x0 + 0.3, 0.3, cx, z0],
      [x1 - x0 + 0.3, 0.3, cx, z1],
      [0.3, z1 - z0 + 0.3, x0, cz],
      [0.3, z1 - z0 + 0.3, x1, cz],
    ] as const) {
      g.add(box(w, 0.07, d, m, x, y, z, false));
    }
  }
  // A few diagonal braces on the front face.
  for (const [ax, bx] of [
    [x0, cx],
    [cx, x1],
  ] as const) {
    const len = Math.hypot(bx - ax, 1.3);
    const brace = box(len, 0.06, 0.06, m, (ax + bx) / 2, 1.95, z1 + 0.05, false);
    brace.rotation.z = Math.atan2(1.3, bx - ax);
    g.add(brace);
  }
  return g;
}

// ─── Procedural furniture ───────────────────────────────────────────────────

const CUSTOM = new Set<string>(['exchangeBoard', 'umbrellaStand', 'shredder', 'paperStack', 'stampPen', 'queuePosts', 'ticketPost', 'mailSacks', 'bin', 'scales']);
const isCustom = (p: string): p is Custom => CUSTOM.has(p);

function custom(p: Custom, accent: string): THREE.Object3D {
  switch (p) {
    case 'exchangeBoard':
      return exchangeBoard();
    case 'umbrellaStand':
      return umbrellaStand(accent);
    case 'shredder':
      return shredder(accent);
    case 'paperStack':
      return paperStack();
    case 'stampPen':
      return stampPen();
    case 'queuePosts':
      return queuePosts();
    case 'ticketPost':
      return ticketPost();
    case 'mailSacks':
      return mailSacks();
    case 'bin':
      return bin();
    case 'scales':
      return scales();
  }
}

/** The Exchange Bureau's rate board: four colour columns with arrows between them. */
function exchangeBoard(): THREE.Object3D {
  const g = new THREE.Group();
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 256;
  const x = c.getContext('2d')!;
  x.fillStyle = '#1d2a26';
  x.fillRect(0, 0, 512, 256);
  x.strokeStyle = '#c9a14e';
  x.lineWidth = 8;
  x.strokeRect(6, 6, 500, 244);
  x.fillStyle = '#e8d9b0';
  x.font = '700 34px "Cinzel", Georgia, serif';
  x.textAlign = 'center';
  x.fillText('TODAY’S RATES', 256, 50);
  const cols = ['#d0483c', '#e2b53e', '#3f7fe0', '#3fa86a'];
  cols.forEach((col, i) => {
    const cx = 80 + i * 118;
    x.fillStyle = col;
    x.beginPath();
    x.arc(cx, 130, 30, 0, Math.PI * 2);
    x.fill();
    x.fillStyle = '#e8d9b0';
    x.font = '700 30px "Cinzel", Georgia, serif';
    x.fillText('1 : 1', cx, 205);
    if (i < 3) {
      x.fillStyle = '#c9a14e';
      x.font = '700 28px Georgia, serif';
      x.fillText('⇄', cx + 59, 140);
    }
  });
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const board = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 1.2), new THREE.MeshBasicMaterial({ map: tex, toneMapped: false }));
  board.position.set(0, 1.85, 0.06);
  board.rotation.x = -0.18;
  const frame = box(2.56, 1.34, 0.08, mat.darkWood(), 0, 1.85, 0, false);
  frame.rotation.x = -0.18;
  g.add(frame, board);
  return g;
}

function umbrellaStand(accent: string): THREE.Object3D {
  const g = new THREE.Group();
  const stand = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.24, 0.7, 16, 1, true), mat.brass());
  stand.position.y = 0.35;
  (stand.material as THREE.Material).side = THREE.DoubleSide;
  g.add(stand);
  const colors = [accent, '#2e2a38', '#8e2231', '#2c5e3c'];
  colors.forEach((col, i) => {
    const a = (i / colors.length) * Math.PI * 2;
    const u = new THREE.Group();
    const shaft = box(0.035, 1.25, 0.035, mat.iron(), 0, 0.62, 0, false);
    const canopy = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.85, 8), toonFrom({ color: col }));
    canopy.position.y = 0.95;
    const hook = new THREE.Mesh(new THREE.TorusGeometry(0.07, 0.022, 6, 12, Math.PI), mat.darkWood());
    hook.position.y = 1.3;
    u.add(shaft, canopy, hook);
    u.position.set(Math.cos(a) * 0.12, 0, Math.sin(a) * 0.12);
    u.rotation.set(Math.sin(a) * 0.18, 0, Math.cos(a) * 0.18);
    g.add(u);
  });
  return g;
}

/** A big clockwork shredder: a hopper, a crank wheel and ribbons of paper spilling into a tray. */
function shredder(accent: string): THREE.Object3D {
  const g = new THREE.Group();
  const body = toonFrom({ color: '#4a5046', metalness: 0.6 });
  g.add(box(1.5, 0.9, 0.9, body, 0, 0.45, 0));
  g.add(box(1.6, 0.12, 1.0, mat.brass(), 0, 0.96, 0, false));
  const hopper = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.42, 0.45, 4, 1), body);
  hopper.rotation.y = Math.PI / 4;
  hopper.position.y = 1.25;
  g.add(hopper);
  const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.05, 8, 20), mat.brass());
  wheel.position.set(0.82, 0.62, 0);
  wheel.rotation.y = Math.PI / 2;
  g.add(wheel);
  for (let i = 0; i < 4; i++) {
    const spoke = box(0.04, 0.64, 0.04, mat.brass(), 0.82, 0.62, 0, false);
    spoke.rotation.x = (i * Math.PI) / 4;
    g.add(spoke);
  }
  const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.08, 10, 8), toonFrom({ color: accent, emissive: accent, emissiveIntensity: 0.6 }));
  lamp.position.set(-0.55, 1.05, 0.46);
  g.add(lamp);
  // Paper ribbons from the mouth into a tray.
  const paper = toonFrom({ color: '#f1ead6' });
  g.add(box(1.3, 0.12, 0.7, mat.darkWood(), 0, 0.06, 0.85));
  for (let i = 0; i < 9; i++) {
    const strip = box(0.07, 0.02, 0.62, paper, -0.5 + i * 0.125, 0.16 + (i % 3) * 0.015, 0.82, false);
    strip.rotation.x = 0.08 * ((i % 2) * 2 - 1);
    g.add(strip);
  }
  const mouth = box(1.1, 0.06, 0.05, toonFrom({ color: '#101010' }), 0, 0.55, 0.46, false);
  g.add(mouth);
  return g;
}

function paperStack(): THREE.Object3D {
  const g = new THREE.Group();
  const paper = toonFrom({ color: '#efe6cf' });
  const tie = toonFrom({ color: '#8e2231' });
  for (let i = 0; i < 3; i++) {
    const s = box(0.62, 0.26, 0.44, paper, (i % 2) * 0.08, 0.13 + i * 0.27, (i % 2) * -0.05);
    s.rotation.y = (i - 1) * 0.12;
    g.add(s);
    g.add(box(0.64, 0.27, 0.05, tie, s.position.x, s.position.y, s.position.z, false));
  }
  return g;
}

/** The pound: a brass-barred pen with runaway rubber stamps inside. */
function stampPen(): THREE.Object3D {
  const g = new THREE.Group();
  const brass = mat.brass();
  const [w, d, h] = [2.6, 1.7, 1.0];
  for (const [x, z] of [
    [-w / 2, -d / 2],
    [w / 2, -d / 2],
    [w / 2, d / 2],
    [-w / 2, d / 2],
  ] as const) {
    g.add(box(0.1, h + 0.1, 0.1, brass, x, (h + 0.1) / 2, z));
  }
  for (const y of [0.12, h]) {
    g.add(box(w, 0.05, 0.05, brass, 0, y, -d / 2, false), box(w, 0.05, 0.05, brass, 0, y, d / 2, false));
    g.add(box(0.05, 0.05, d, brass, -w / 2, y, 0, false), box(0.05, 0.05, d, brass, w / 2, y, 0, false));
  }
  for (let i = 1; i < 10; i++) {
    const x = -w / 2 + (w * i) / 10;
    g.add(box(0.03, h, 0.03, brass, x, h / 2, -d / 2, false), box(0.03, h, 0.03, brass, x, h / 2, d / 2, false));
  }
  for (let i = 1; i < 6; i++) {
    const z = -d / 2 + (d * i) / 6;
    g.add(box(0.03, h, 0.03, brass, -w / 2, h / 2, z, false), box(0.03, h, 0.03, brass, w / 2, h / 2, z, false));
  }
  // Straw on the floor of the pen.
  g.add(box(w - 0.1, 0.04, d - 0.1, toonFrom({ color: '#d6b25a' }), 0, 0.02, 0, false));
  const spots: [number, number, number, number][] = [
    [-0.8, -0.3, 0.2, 0.3],
    [-0.1, 0.35, -0.4, 0],
    [0.7, -0.2, 0.9, -0.2],
    [0.2, -0.4, 2.2, 0.15],
    [-0.6, 0.45, 1.4, -0.35],
  ];
  const wood = toonFrom({ color: '#3a2216' });
  const pad = toonFrom({ color: '#9e1f2c' });
  for (const [x, z, ry, tilt] of spots) {
    const s = new THREE.Group();
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.22, 0.1, 16), brass);
    base.position.y = 0.09;
    const rubber = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.19, 0.05, 16), pad);
    rubber.position.y = 0.025;
    const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.07, 0.3, 12), wood);
    grip.position.y = 0.3;
    const knob = new THREE.Mesh(new THREE.SphereGeometry(0.1, 12, 10), brass);
    knob.position.y = 0.47;
    s.add(base, rubber, grip, knob);
    s.position.set(x, 0.04, z);
    s.rotation.set(tilt, ry, tilt * 0.5);
    g.add(s);
  }
  return g;
}

function queuePosts(): THREE.Object3D {
  const g = new THREE.Group();
  const rope = toonFrom({ color: '#9a2f35' });
  const xs = [-1.2, 0, 1.2];
  for (const x of xs) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.95, 10), mat.brass());
    post.position.set(x, 0.48, 0);
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.2, 0.06, 16), mat.brass());
    foot.position.set(x, 0.03, 0);
    const knob = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 8), mat.brass());
    knob.position.set(x, 0.98, 0);
    g.add(post, foot, knob);
  }
  for (let i = 0; i < xs.length - 1; i++) {
    const curve = new THREE.QuadraticBezierCurve3(new THREE.Vector3(xs[i]!, 0.9, 0), new THREE.Vector3((xs[i]! + xs[i + 1]!) / 2, 0.66, 0), new THREE.Vector3(xs[i + 1]!, 0.9, 0));
    g.add(new THREE.Mesh(new THREE.TubeGeometry(curve, 12, 0.03, 6), rope));
  }
  return g;
}

function ticketPost(): THREE.Object3D {
  const g = new THREE.Group();
  const red = toonFrom({ color: '#b8342e' });
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 1.2, 10), mat.iron());
  post.position.y = 0.6;
  const head = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.16, 16), red);
  head.rotation.x = Math.PI / 2;
  head.position.set(0, 1.25, 0);
  const slip = box(0.12, 0.2, 0.01, toonFrom({ color: '#f5efe0' }), 0, 1.08, 0.09, false);
  g.add(post, head, slip);
  return g;
}

function mailSacks(): THREE.Object3D {
  const g = new THREE.Group();
  const canvas = toonFrom({ color: '#cbb48a' });
  const tie = toonFrom({ color: '#6a4a2a' });
  const spots: [number, number, number][] = [
    [0, 0, 1],
    [0.55, 0.25, 0.85],
    [-0.1, 0.62, 0.9],
  ];
  for (const [x, z, k] of spots) {
    const sack = new THREE.Mesh(new THREE.SphereGeometry(0.42, 14, 10), canvas);
    sack.scale.set(k, 1.05 * k, 0.85 * k);
    sack.position.set(x, 0.4 * k, z);
    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.1 * k, 0.16 * k, 0.22 * k, 10), canvas);
    neck.position.set(x, 0.86 * k, z);
    const band = new THREE.Mesh(new THREE.TorusGeometry(0.11 * k, 0.025, 6, 14), tie);
    band.rotation.x = Math.PI / 2;
    band.position.set(x, 0.8 * k, z);
    g.add(sack, neck, band);
  }
  return g;
}

function bin(): THREE.Object3D {
  const g = new THREE.Group();
  const b = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.25, 0.6, 16, 1, true), toonFrom({ color: '#5a6250', side: THREE.DoubleSide }));
  b.position.y = 0.3;
  g.add(b);
  const paper = toonFrom({ color: '#f1ead6' });
  for (let i = 0; i < 7; i++) {
    const s = box(0.05, 0.22, 0.02, paper, Math.cos(i) * 0.12, 0.62, Math.sin(i * 1.7) * 0.12, false);
    s.rotation.z = (i % 3) * 0.3 - 0.3;
    g.add(s);
  }
  return g;
}

/** A brass balance on a pedestal (the Exchange Bureau). */
function scales(): THREE.Object3D {
  const g = new THREE.Group();
  const brass = mat.brass();
  const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.3, 0.9, 12), mat.darkWood());
  ped.position.y = 0.45;
  const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.7, 8), brass);
  stem.position.y = 1.25;
  const beam = box(0.9, 0.04, 0.04, brass, 0, 1.58, 0, false);
  g.add(ped, stem, beam);
  for (const e of [-1, 1]) {
    const pan = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.12, 0.05, 16), brass);
    pan.position.set(e * 0.42, 1.3, 0);
    const chain = box(0.01, 0.28, 0.01, brass, e * 0.42, 1.44, 0, false);
    g.add(pan, chain);
  }
  return g;
}
